import { DateInput } from "@/common/components/DateInput/DateInput";
import Modal from "@/common/components/Modal";
import { UnitTextField } from "@/common/components/UnitTextField";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useCreateMaintenanceEstablishmentChoiceAuctionEntryProposal } from "../../../hooks/useCreateMaintenanceEstablishmentChoiceAuctionEntryProposal";
import { auctionEntryProposalSchema, AuctionEntryProposalValidationSchema } from "../../../schemas/auctionEntryProposal.schema";
import TimeInput from "@/common/components/TimeInput";

interface CreateAuctionProposalProps {
  auctionEntryId: string | undefined;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const CreateAuctionProposal = ({
  auctionEntryId,
  isOpen,
  setIsOpen,
}: CreateAuctionProposalProps) => {
  const queryClient = useQueryClient();

  const {
    setMessages,
  } = useModalMessagesSimple({
    isError: true,
  });

  const {
    control,
    setError,
    handleSubmit,
    reset,
    formState: {
      errors,
    },
  } = useForm<AuctionEntryProposalValidationSchema>({
    resolver: zodResolver(auctionEntryProposalSchema),
    defaultValues: {
      auction_entry_id: auctionEntryId,
      directive_proposal_content_attributes: {},
    },
  });

  const {
    mutate: createProposal,
    isPending: createProposalIsPending,
  } = useCreateMaintenanceEstablishmentChoiceAuctionEntryProposal({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', auctionEntryId],
        });

        setIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      },
    }
  });

  const onSubmit: SubmitHandler<AuctionEntryProposalValidationSchema> = (data) => createProposal(data);

  useEffect(() => {
    reset({
      auction_entry_id: auctionEntryId,
      directive_proposal_content_attributes: {},
    });
  }, [isOpen, auctionEntryId]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Adicionar proposta"
      titleClass="text-2xl"
      className="rounded-lg overflow-hidden"
      bodyClassName="text-white-800"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-200 rounded-bl-lg"
      confirmButtonIsLoading={createProposalIsPending}
      onConfirm={handleSubmit(onSubmit)}
      body={(
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <DateInput
              fieldName="directive_proposal_content_attributes.start_at_date"
              control={control}
              label="Data sugerida"
              className="flex-1"
              markAsRequired
              errorMessage={errors?.directive_proposal_content_attributes?.start_at_date?.message}
            />

            <TimeInput
              fieldName="directive_proposal_content_attributes.start_at_time"
              control={control}
              label="Horário sugerido"
              markAsRequired
              className="flex-1"
              errorMessage={errors?.directive_proposal_content_attributes?.start_at_time?.message}
            />
          </div>

          <Controller
            control={control}
            name="directive_proposal_content_attributes.deadline_to_complete_in_days"
            render={({ field }) => (
              <UnitTextField
                decimals={0}
                placeholder="Escreva aqui o prazo em dias"
                unit="dia(s)"
                label="Prazo em dias para envio do diagnostico e orçamento pós-check-in"
                markAsRequired
                {...field}
                errorMessage={errors?.directive_proposal_content_attributes?.deadline_to_complete_in_days?.message}
              />
            )}
          />
        </div>
      )}
    />
  );
}

export default CreateAuctionProposal;
